// The colour vocabulary. Two different hex values per colour on purpose:
//
//  - `lab` is what the phone shows as the "card". Every lab colour is tuned to the
//    same relative luminance (see tests/colors.test.ts), so brightness leaking
//    through a mask can't reveal which colour is up. Pure yellow is about 13x brighter
//    than pure blue, and that gap is a far easier cue than any colour perception.
//  - `ui` is the vivid version used in the dashboard and buttons.

export type ColorId =
  | "red" | "orange" | "yellow" | "green" | "blue" | "purple" | "pink" | "white" | "black";

export interface ColorDef {
  id: ColorId;
  label: string;
  ui: string;
  lab: string;
  aliases: string[];
  warm: boolean | null;
}

export const COLORS: Record<ColorId, ColorDef> = {
  red:    { id: "red",    label: "Red",    ui: "#e5484d", lab: "#ff5e5e", warm: true,  aliases: ["read"] },
  orange: { id: "orange", label: "Orange", ui: "#f76b15", lab: "#ef7000", warm: true,  aliases: ["orang"] },
  yellow: { id: "yellow", label: "Yellow", ui: "#f5c518", lab: "#a19800", warm: true,  aliases: ["yello"] },
  green:  { id: "green",  label: "Green",  ui: "#30a46c", lab: "#00ad29", warm: false, aliases: ["grean", "greene"] },
  blue:   { id: "blue",   label: "Blue",   ui: "#3b82f6", lab: "#5792ff", warm: false, aliases: ["blew"] },
  purple: { id: "purple", label: "Purple", ui: "#8e4ec6", lab: "#be71ff", warm: false, aliases: ["violet"] },
  pink:   { id: "pink",   label: "Pink",   ui: "#e93d82", lab: "#ff51a6", warm: true,  aliases: ["pinks"] },
  white:  { id: "white",  label: "White",  ui: "#f0efec", lab: "#ffffff", warm: null,  aliases: ["wite"] },
  black:  { id: "black",  label: "Black",  ui: "#26262a", lab: "#000000", warm: null,  aliases: ["blok"] },
};

export const ALL_COLOR_IDS = Object.keys(COLORS) as ColorId[];

/** Default deck: four colours, chance = 25%. Chosen to be far apart in hue. */
export const DEFAULT_PALETTE: ColorId[] = ["red", "yellow", "green", "blue"];

/** Colours the phone can display at matched luminance. White and black can't be matched. */
export const LAB_SAFE: ColorId[] = ["red", "orange", "yellow", "green", "blue", "purple", "pink"];

export function relativeLuminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * c[0]! + 0.7152 * c[1]! + 0.0722 * c[2]!;
}

export function textOn(hex: string): string {
  return relativeLuminance(hex) > 0.35 ? "#0b0b0b" : "#ffffff";
}
